import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Save, ArrowLeft, Plus, X, Image, Package } from 'lucide-react';
import PageHeader from '../../components/admin/PageHeader';
import ContentCard from '../../components/admin/ContentCard';
import LoadingState from '../../components/admin/LoadingState';
import RichTextEditor from '../../components/admin/RichTextEditor';
import toast from 'react-hot-toast';
import { createSlug } from '../../utils/slugify';

const ProductForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [product, setProduct] = useState({
    name: '',
    type: 'moto',
    model: '',
    slug: '',
    description: '',
    price: 0,
    images: [] as string[],
    specifications: {
      autonomie: '',
      vitesse: '',
      poids: '',
      puissance: '',
      batterie: '',
      temps_charge: '',
      suspension: '',
      freins: '',
      pneus: '',
      éclairage: '',
      affichage: '',
      connectivité: '',
      avantages: [] as string[]
    }
  });
  
  const [newAdvantage, setNewAdvantage] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');

  useEffect(() => {
    document.title = isEditing ? 'Modifier un Produit | Admin' : 'Ajouter un Produit | Admin';
    
    if (isEditing) {
      loadProduct();
    }
  }, [isEditing, id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      if (data) {
        // Ensure specifications has all required fields
        const specs = data.specifications || {};
        const defaultSpecs = {
          autonomie: '',
          vitesse: '',
          poids: '',
          puissance: '',
          batterie: '',
          temps_charge: '',
          suspension: '',
          freins: '',
          pneus: '',
          éclairage: '',
          affichage: '',
          connectivité: '',
          avantages: []
        };
        
        setProduct({
          ...data,
          specifications: { ...defaultSpecs, ...specs }
        });
      }
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Erreur lors du chargement du produit');
      navigate('/admin/products');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'price') {
      setProduct({ ...product, [name]: parseInt(value) || 0 });
    } else {
      setProduct({ ...product, [name]: value });
    }
    
    // Auto-generate slug from name
    if (name === 'name' && !isEditing) {
      setProduct(prev => ({ ...prev, slug: createSlug(value) }));
    }
  };

  const handleSpecChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProduct({
      ...product,
      specifications: {
        ...product.specifications,
        [name]: value
      }
    });
  };

  const handleAddAdvantage = () => {
    if (!newAdvantage.trim()) return;
    
    setProduct({
      ...product,
      specifications: {
        ...product.specifications,
        avantages: [...(product.specifications.avantages || []), newAdvantage]
      }
    });
    
    setNewAdvantage('');
  };

  const handleRemoveAdvantage = (index: number) => {
    const newAdvantages = [...product.specifications.avantages];
    newAdvantages.splice(index, 1);
    
    setProduct({
      ...product,
      specifications: {
        ...product.specifications,
        avantages: newAdvantages
      }
    });
  };

  const handleAddImage = () => {
    if (!newImageUrl.trim()) return;
    
    setProduct({
      ...product,
      images: [...product.images, newImageUrl]
    });
    
    setNewImageUrl('');
  };

  const handleRemoveImage = (index: number) => {
    const newImages = [...product.images];
    newImages.splice(index, 1);
    
    setProduct({
      ...product,
      images: newImages
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Validate required fields
      if (!product.name || !product.type || !product.model || !product.slug || !product.description || product.price <= 0) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }
      
      let response;
      
      if (isEditing) {
        // Update existing product
        response = await supabase
          .from('products')
          .update({
            name: product.name,
            type: product.type,
            model: product.model,
            slug: product.slug,
            description: product.description,
            specifications: product.specifications,
            price: product.price,
            images: product.images
          })
          .eq('id', id);
      } else {
        // Insert new product
        response = await supabase
          .from('products')
          .insert([{
            name: product.name,
            type: product.type,
            model: product.model,
            slug: product.slug,
            description: product.description,
            specifications: product.specifications,
            price: product.price,
            images: product.images
          }]);
      }
      
      if (response.error) throw response.error;
      
      toast.success(isEditing ? 'Produit mis à jour avec succès' : 'Produit ajouté avec succès');
      navigate('/admin/products');
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Erreur lors de l\'enregistrement du produit');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="px-6">
        <PageHeader
          title={isEditing ? 'Modifier le Produit' : 'Ajouter un Produit'}
          backLink="/admin/products"
        />
        <LoadingState type="form" />
      </div>
    );
  }

  return (
    <div className="px-6">
      <PageHeader
        title={isEditing ? 'Modifier le Produit' : 'Ajouter un Produit'}
        backLink="/admin/products"
      />
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            <ContentCard title="Informations Générales">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Nom du produit *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={product.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-1">
                      Modèle *
                    </label>
                    <input
                      type="text"
                      id="model"
                      name="model"
                      value={product.model}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                      Type *
                    </label>
                    <select
                      id="type"
                      name="type"
                      value={product.type}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    >
                      <option value="moto">Moto</option>
                      <option value="accessory">Accessoire</option>
                      <option value="part">Pièce détachée</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                      Prix (FCFA) *
                    </label>
                    <input
                      type="number"
                      id="price"
                      name="price"
                      value={product.price}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      min="0"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
                    Slug URL *
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      /products/
                    </span>
                    <input
                      type="text"
                      id="slug"
                      name="slug"
                      value={product.slug}
                      onChange={handleChange}
                      className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    L'URL unique de ce produit (générée automatiquement à partir du nom)
                  </p>
                </div>
                
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={product.description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
              </div>
            </ContentCard>
          </div>
          
          <div>
            <ContentCard title="Images du Produit">
              <div className="space-y-4">
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label htmlFor="newImageUrl" className="block text-sm font-medium text-gray-700 mb-1">
                      Ajouter une image (URL)
                    </label>
                    <input
                      type="url"
                      id="newImageUrl"
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddImage}
                    className="px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    <Plus size={16} />
                  </button>
                </div>
                
                <div className="border border-gray-200 rounded-md p-4">
                  {product.images.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {product.images.map((image, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={image}
                            alt={`Product ${index + 1}`}
                            className="w-full h-24 object-cover rounded-md"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150';
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index)}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-6 text-gray-400">
                      <Image size={32} className="mb-2" />
                      <p className="text-sm">Aucune image ajoutée</p>
                    </div>
                  )}
                </div>
              </div>
            </ContentCard>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            <ContentCard title="Spécifications Techniques">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="autonomie" className="block text-sm font-medium text-gray-700 mb-1">
                      Autonomie
                    </label>
                    <input
                      type="text"
                      id="autonomie"
                      name="autonomie"
                      value={product.specifications.autonomie}
                      onChange={handleSpecChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="ex: 120 km"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="vitesse" className="block text-sm font-medium text-gray-700 mb-1">
                      Vitesse Maximale
                    </label>
                    <input
                      type="text"
                      id="vitesse"
                      name="vitesse"
                      value={product.specifications.vitesse}
                      onChange={handleSpecChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="ex: 45 km/h"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="poids" className="block text-sm font-medium text-gray-700 mb-1">
                      Poids
                    </label>
                    <input
                      type="text"
                      id="poids"
                      name="poids"
                      value={product.specifications.poids}
                      onChange={handleSpecChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="ex: 95 kg"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="puissance" className="block text-sm font-medium text-gray-700 mb-1">
                      Puissance
                    </label>
                    <input
                      type="text"
                      id="puissance"
                      name="puissance"
                      value={product.specifications.puissance}
                      onChange={handleSpecChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="ex: 3000W"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="batterie" className="block text-sm font-medium text-gray-700 mb-1">
                      Batterie
                    </label>
                    <input
                      type="text"
                      id="batterie"
                      name="batterie"
                      value={product.specifications.batterie}
                      onChange={handleSpecChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="ex: 72V 30Ah Lithium-ion"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="temps_charge" className="block text-sm font-medium text-gray-700 mb-1">
                      Temps de Charge
                    </label>
                    <input
                      type="text"
                      id="temps_charge"
                      name="temps_charge"
                      value={product.specifications.temps_charge}
                      onChange={handleSpecChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="ex: 4-6 heures"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="suspension" className="block text-sm font-medium text-gray-700 mb-1">
                      Suspension
                    </label>
                    <input
                      type="text"
                      id="suspension"
                      name="suspension"
                      value={product.specifications.suspension}
                      onChange={handleSpecChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="ex: Fourche hydraulique avant, Double amortisseur arrière"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="freins" className="block text-sm font-medium text-gray-700 mb-1">
                      Freins
                    </label>
                    <input
                      type="text"
                      id="freins"
                      name="freins"
                      value={product.specifications.freins}
                      onChange={handleSpecChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="ex: Disque hydraulique avant et arrière"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="pneus" className="block text-sm font-medium text-gray-700 mb-1">
                      Pneus
                    </label>
                    <input
                      type="text"
                      id="pneus"
                      name="pneus"
                      value={product.specifications.pneus}
                      onChange={handleSpecChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="ex: Tubeless tout-terrain 90/90-14"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="éclairage" className="block text-sm font-medium text-gray-700 mb-1">
                      Éclairage
                    </label>
                    <input
                      type="text"
                      id="éclairage"
                      name="éclairage"
                      value={product.specifications.éclairage}
                      onChange={handleSpecChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="ex: Full LED avec feux de jour"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="affichage" className="block text-sm font-medium text-gray-700 mb-1">
                      Affichage
                    </label>
                    <input
                      type="text"
                      id="affichage"
                      name="affichage"
                      value={product.specifications.affichage}
                      onChange={handleSpecChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="ex: Écran LCD couleur"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="connectivité" className="block text-sm font-medium text-gray-700 mb-1">
                      Connectivité
                    </label>
                    <input
                      type="text"
                      id="connectivité"
                      name="connectivité"
                      value={product.specifications.connectivité}
                      onChange={handleSpecChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="ex: Bluetooth, Application mobile"
                    />
                  </div>
                </div>
              </div>
            </ContentCard>
          </div>
          
          <div>
            <ContentCard title="Avantages">
              <div className="space-y-4">
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label htmlFor="newAdvantage" className="block text-sm font-medium text-gray-700 mb-1">
                      Ajouter un avantage
                    </label>
                    <input
                      type="text"
                      id="newAdvantage"
                      value={newAdvantage}
                      onChange={(e) => setNewAdvantage(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="ex: Premier mois gratuit"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddAdvantage}
                    className="px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    <Plus size={16} />
                  </button>
                </div>
                
                <div className="border border-gray-200 rounded-md p-4">
                  {product.specifications.avantages && product.specifications.avantages.length > 0 ? (
                    <ul className="space-y-2">
                      {product.specifications.avantages.map((advantage, index) => (
                        <li key={index} className="flex items-center justify-between group">
                          <span className="text-sm text-gray-700">{advantage}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveAdvantage(index)}
                            className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-6 text-gray-400">
                      <Package size={32} className="mb-2" />
                      <p className="text-sm">Aucun avantage ajouté</p>
                    </div>
                  )}
                </div>
              </div>
            </ContentCard>
          </div>
        </div>
        
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/products')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center"
          >
            {saving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Enregistrement...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                {isEditing ? 'Mettre à jour' : 'Enregistrer'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;